import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _reduceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/reduce';
import { c as clsx } from '../clsx.m-7e9a4a81.js';
import React__default, { useState, useRef, useMemo } from 'react';
import { useEventCallback } from '@thimble/utility';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _includesInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/includes';
import { useUnmount } from 'react-use';
import '@babel/runtime-corejs3/helpers/typeof';
import '@babel/runtime-corejs3/core-js-stable/array/is-array';

// https://github.com/element-plus/element-plus/blob/a57727bfa41943bc4bf81a2bc31d6895362b5077/packages/scrollbar/src/util.ts#L1
var AXIS_MAP = {
  vertical: {
    offsetSize: 'offsetHeight',
    scrollValue: 'scrollTop',
    scrollSize: 'scrollHeight',
    size: 'height',
    axis: 'Y',
    mouseEventClientValue: 'clientY',
    clientRectStart: 'top'
  },
  horizontal: {
    offsetSize: 'offsetWidth',
    scrollValue: 'scrollLeft',
    scrollSize: 'scrollWidth',
    size: 'width',
    axis: 'X',
    mouseEventClientValue: 'clientX',
    clientRectStart: 'left'
  }
}; // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types

var useSlider = function useSlider(_ref) {
  _ref.direction;
      var onChange = _ref.onChange;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      active = _useState2[0],
      setActive = _useState2[1];

  var position = function position(e) {
    return 'touches' in e ? {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    } : {
      x: e.clientX,
      y: e.clientY
    };
  };

  var handleDrag = useEventCallback(function (e) {
    var mouse = position(e);
    onChange === null || onChange === void 0 ? void 0 : onChange({
      mouse: mouse
    });
  });
  var handleEnd = useEventCallback(function () {
    setActive(false);
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleEnd);
    document.removeEventListener('touchmove', handleDrag);
    document.removeEventListener('touchend', handleEnd);
    document.removeEventListener('touchcancel', handleEnd);
  });
  var handleStart = useEventCallback(function (e) {
    var _context;

    if (e.ctrlKey || 'button' in e && _includesInstanceProperty(_context = [1, 2]).call(_context, e.button)) {
      return;
    }

    setActive(true);
    handleDrag(e.nativeEvent);
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleDrag);
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
  });
  useUnmount(handleEnd); // component could unexpectly unmount during dragging.

  return {
    active: active,
    handleStart: handleStart,
    handleDrag: handleDrag,
    handleEnd: handleEnd
  };
};

var defaultRenderLabel = function defaultRenderLabel(_ref) {
  var value = _ref.value,
      label = _ref.label;
  return label || value;
};

var noop = function noop() {};

var EMPTY = [];

var Slider = function Slider(_ref2) {
  var _ref2$value = _ref2.value,
      value = _ref2$value === void 0 ? 0 : _ref2$value,
      _ref2$onChange = _ref2.onChange,
      onChange = _ref2$onChange === void 0 ? noop : _ref2$onChange,
      _ref2$min = _ref2.min,
      min = _ref2$min === void 0 ? 0 : _ref2$min,
      _ref2$max = _ref2.max,
      max = _ref2$max === void 0 ? 100 : _ref2$max,
      _ref2$step = _ref2.step,
      step = _ref2$step === void 0 ? 1 : _ref2$step,
      tooltip = _ref2.tooltip,
      _ref2$renderLabel = _ref2.renderLabel,
      renderLabel = _ref2$renderLabel === void 0 ? defaultRenderLabel : _ref2$renderLabel,
      _ref2$label = _ref2.label,
      label = _ref2$label === void 0 ? EMPTY : _ref2$label;
  var percentage = (value - min) / (max - min) * 100;
  var clamp = useEventCallback(function (v) {
    var _min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : min;

    var _max = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : max;

    return Math.max(_min, Math.min(v, _max));
  });
  var railRef = useRef(null);
  var labelWithPosition = useMemo(function () {
    return _mapInstanceProperty(label).call(label, function (_ref3) {
      var value = _ref3.value,
          label = _ref3.label;
      return {
        value: value,
        label: label,
        percentage: Math.round((value - min) / (max - min) * 100)
      };
    });
  }, [label, max, min]);
  var handleSliderChange = useEventCallback(function (_ref4) {
    var _ref4$mouse = _ref4.mouse,
        x = _ref4$mouse.x;
        _ref4$mouse.y;
    if (!railRef.current) return;

    var _railRef$current$getB = railRef.current.getBoundingClientRect(),
        width = _railRef$current$getB.width,
        left = _railRef$current$getB.left;

    var percentage = (x - left) / width;

    if (typeof step === 'number') {
      var v = clamp(step * Math.round(percentage * (max - min) / step) + min);
      onChange(v);
    } else {
      onChange(labelWithPosition[findNearesetIndex(_mapInstanceProperty(labelWithPosition).call(labelWithPosition, function (_ref5) {
        var percentage = _ref5.percentage;
        return percentage;
      }), percentage * 100)].value);
    }
  });

  var _useSlider = useSlider({
    direction: AXIS_MAP.horizontal,
    onChange: handleSliderChange
  }),
      active = _useSlider.active,
      handleStart = _useSlider.handleStart;

  var prev = function prev() {
    if (step === null) {
      var currentIndex = findNearesetIndex(_mapInstanceProperty(labelWithPosition).call(labelWithPosition, function (_ref6) {
        var percentage = _ref6.percentage;
        return percentage;
      }), percentage);
      onChange(labelWithPosition[clamp(currentIndex - 1, 0, labelWithPosition.length - 1)].value);
    } else {
      onChange(clamp(value - step));
    }
  };

  var next = function next() {
    if (step === null) {
      var currentIndex = findNearesetIndex(_mapInstanceProperty(labelWithPosition).call(labelWithPosition, function (_ref7) {
        var percentage = _ref7.percentage;
        return percentage;
      }), percentage);
      onChange(labelWithPosition[clamp(currentIndex + 1, 0, labelWithPosition.length - 1)].value);
    } else {
      onChange(clamp(value + step));
    }
  };

  var handleKeydown = function handleKeydown(e) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        next();
        break;

      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        prev();
        break;
    }
  };

  return /*#__PURE__*/React__default.createElement("div", {
    className: clsx('smb-slider', active && 'smb-slider--active')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "smb-slider__labels"
  }, _mapInstanceProperty(labelWithPosition).call(labelWithPosition, function (_ref8) {
    var label = _ref8.label,
        v = _ref8.value,
        percentage = _ref8.percentage;
    return /*#__PURE__*/React__default.createElement("div", {
      className: "smb-slider__label-item",
      key: v,
      onClick: function onClick() {
        return onChange(v);
      },
      style: {
        left: percentage + '%'
      }
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "smb-slider__label-item__inner"
    }, renderLabel({
      active: v <= value,
      exact: value === v,
      label: label,
      value: v,
      percentage: percentage
    })));
  })), /*#__PURE__*/React__default.createElement("input", {
    type: "range",
    className: "smb-slider__input-el",
    onKeyDown: handleKeydown
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "smb-slider__rail",
    ref: railRef,
    onMouseDown: handleStart
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "smb-slider__fill",
    style: {
      width: percentage + '%'
    }
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "smb-slider__thumb",
    style: {
      left: percentage + '%'
    },
    onTouchStart: function onTouchStart(e) {
      e.stopPropagation();
      handleStart(e);
    }
  }, tooltip ? /*#__PURE__*/React__default.createElement("div", {
    className: "smb-slider__tooltip"
  }, value) : null)));
};

var findNearesetIndex = function findNearesetIndex(valueArray, percentage) {
  if (percentage <= valueArray[0]) return 0;
  if (percentage >= valueArray[valueArray.length - 1]) return valueArray.length - 1;
  return _reduceInstanceProperty(valueArray).call(valueArray, function (ans, value, index) {
    if (typeof ans === 'number') return ans;

    if (index !== valueArray.length - 1) {
      var nextValue = valueArray[index + 1];

      if (value <= percentage && percentage < nextValue) {
        return percentage - value > nextValue - percentage ? index + 1 : index;
      }
    }
  }, undefined);
};

export default Slider;
